import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Layers, Inbox, Mail, TrendingUp, DollarSign, Users, Eye, UserCheck, Repeat } from 'lucide-react';
import {
    ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { api } from '../../lib/api';
import { SegmentedTabs } from '../../components/admin/SegmentedTabs';
import { useCurrency } from '../../context/CurrencyContext';

const BURGUNDY = '#5E2134';
const MAUVE = '#B8949C';
const ROSE = '#FEF2F5';
const STATUS_COLORS = {
    pending: '#B7791F', confirmed: '#1F7A5A', shipped: '#5A2432', delivered: '#1A1416', cancelled: '#B42318',
};

function Stat({ icon: Icon, label, value, hint }) {
    return (
        <div className="luxury-card p-5">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="uppercase font-medium leading-tight break-words" style={{ color: 'var(--ink-muted)', fontSize: '0.6rem', letterSpacing: '0.05em' }}>{label}</p>
                    <p className="font-serif text-4xl mt-1" style={{ color: 'var(--brand-burgundy)' }}>{value}</p>
                    {hint && <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>{hint}</p>}
                </div>
                <div className="p-2 rounded-lg flex-shrink-0" style={{ backgroundColor: 'var(--brand-rose)' }}>
                    <Icon className="w-5 h-5" style={{ color: 'var(--brand-burgundy)' }} />
                </div>
            </div>
        </div>
    );
}

function ChartCard({ title, subtitle, children, testId }) {
    return (
        <div className="luxury-card p-6" data-testid={testId}>
            <div className="mb-4">
                <p className="eyebrow" style={{ color: 'var(--ink-muted)' }}>{subtitle}</p>
                <h2 className="font-serif text-2xl" style={{ color: 'var(--brand-burgundy)' }}>{title}</h2>
            </div>
            {children}
        </div>
    );
}

const tooltipStyle = {
    backgroundColor: '#fff',
    border: '1px solid rgba(90,36,50,0.15)',
    borderRadius: '10px',
    fontSize: '12px',
};

const fmtDate = (d) => { const [, m, day] = d.split('-'); return `${m}/${day}`; };

export default function AdminDashboard() {
    const [data, setData] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [traffic, setTraffic] = useState(null);
    const [range, setRange] = useState(30);
    const { format } = useCurrency();

    useEffect(() => { api.get('/admin/dashboard').then((r) => setData(r.data)); }, []);
    useEffect(() => {
        api.get('/admin/analytics', { params: { days: range } }).then((r) => setAnalytics(r.data));
        api.get('/admin/analytics/traffic', { params: { days: range } }).then((r) => setTraffic(r.data));
    }, [range]);

    if (!data) return <div className="eyebrow" style={{ color: 'var(--ink-muted)' }}>Loading...</div>;

    const ts = analytics?.timeseries || [];
    const statusData = (analytics?.status_breakdown || []).map((s) => ({ ...s, name: s.status }));
    const topProducts = analytics?.top_products || [];
    const trafficTs = traffic?.timeseries || [];
    const retention = traffic?.retention ? [
        { name: 'New', value: traffic.retention.new },
        { name: 'Returning', value: traffic.retention.returning },
    ] : [];
    const funnel = traffic?.funnel ? [
        { name: 'Visits', value: traffic.funnel.visits },
        { name: 'Product Views', value: traffic.funnel.product_views },
        { name: 'Add to Cart', value: traffic.funnel.add_to_carts },
        { name: 'Requests', value: traffic.funnel.requests },
    ] : [];
    const topViewed = traffic?.top_viewed_products || [];

    return (
        <div data-testid="admin-dashboard">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                <div>
                    <p className="eyebrow" style={{ color: 'var(--brand-burgundy)' }}>Atelier</p>
                    <h1 className="font-serif text-4xl mt-1" style={{ color: 'var(--brand-burgundy)' }}>Dashboard</h1>
                </div>
                <SegmentedTabs
                    value={range}
                    onChange={setRange}
                    options={[{ key: 7, label: '7d' }, { key: 30, label: '30d' }, { key: 90, label: '90d' }]}
                    testIdPrefix="dashboard-range"
                    size="sm"
                />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                <Stat icon={DollarSign} label={`Revenue (${range}d)`} value={format(analytics?.total_revenue || 0)} hint={`${analytics?.total_requests || 0} requests`} />
                <Stat icon={ShoppingBag} label="Products" value={data.products_count} hint={`${data.active_products} active`} />
                <Stat icon={Inbox} label="Requests" value={data.requests_total} hint={`${data.requests_pending} pending`} />
                <Stat icon={Mail} label="Subscribers" value={data.subscribers_count} hint={`+${analytics?.new_subscribers || 0} in ${range}d`} />
            </div>

            {/* Revenue + Requests trends */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                <ChartCard title="Revenue Over Time" subtitle="Trend" testId="dashboard-chart-revenue">
                    <ResponsiveContainer width="100%" height={260}>
                        <AreaChart data={ts} margin={{ top: 6, right: 8, left: -12, bottom: 0 }}>
                            <defs>
                                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={BURGUNDY} stopOpacity={0.35} />
                                    <stop offset="100%" stopColor={BURGUNDY} stopOpacity={0.02} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(90,36,50,0.08)" />
                            <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11, fill: '#9A8088' }} interval="preserveStartEnd" minTickGap={24} />
                            <YAxis tick={{ fontSize: 11, fill: '#9A8088' }} />
                            <Tooltip contentStyle={tooltipStyle} formatter={(v) => [format(v), 'Revenue']} labelFormatter={fmtDate} />
                            <Area type="monotone" dataKey="revenue" stroke={BURGUNDY} strokeWidth={2} fill="url(#revGrad)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Requests & Subscribers" subtitle="Volume" testId="dashboard-chart-requests">
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={ts} margin={{ top: 6, right: 8, left: -12, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(90,36,50,0.08)" />
                            <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11, fill: '#9A8088' }} interval="preserveStartEnd" minTickGap={24} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#9A8088' }} />
                            <Tooltip contentStyle={tooltipStyle} labelFormatter={fmtDate} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Bar dataKey="requests" name="Requests" fill={BURGUNDY} radius={[4, 4, 0, 0]} />
                            <Bar dataKey="subscribers" name="Subscribers" fill={MAUVE} radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>

            {/* Status donut + Top products */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                <ChartCard title="Status Breakdown" subtitle="Pipeline" testId="dashboard-chart-status">
                    {statusData.length === 0 ? (
                        <p className="text-sm py-12 text-center" style={{ color: 'var(--ink-muted)' }}>No requests in this period.</p>
                    ) : (
                        <ResponsiveContainer width="100%" height={260}>
                            <PieChart>
                                <Pie data={statusData} dataKey="count" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                                    {statusData.map((s) => <Cell key={s.name} fill={STATUS_COLORS[s.status] || BURGUNDY} />)}
                                </Pie>
                                <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [v, n]} />
                                <Legend wrapperStyle={{ fontSize: 12, textTransform: 'capitalize' }} />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>

                <ChartCard title="Top Products" subtitle="By units sold" testId="dashboard-chart-top-products">
                    {topProducts.length === 0 ? (
                        <p className="text-sm py-12 text-center" style={{ color: 'var(--ink-muted)' }}>No sales in this period.</p>
                    ) : (
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={topProducts} layout="vertical" margin={{ top: 6, right: 16, left: 8, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(90,36,50,0.08)" horizontal={false} />
                                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#9A8088' }} />
                                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: '#5E2134' }} />
                                <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => n === 'units' ? [v, 'Units'] : [format(v), 'Revenue']} />
                                <Bar dataKey="units" name="units" fill={MAUVE} radius={[0, 4, 4, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>
            </div>

            {/* Visitor Analytics */}
            <div className="mt-10 mb-2">
                <p className="eyebrow" style={{ color: 'var(--brand-burgundy)' }}>Behavior</p>
                <h2 className="font-serif text-2xl sm:text-3xl" style={{ color: 'var(--brand-burgundy)' }}>Visitor Analytics</h2>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6">
                <Stat icon={Users} label="Visits" value={traffic?.total_visits ?? 0} hint={`in ${range} days`} />
                <Stat icon={Eye} label="Page Views" value={traffic?.total_pageviews ?? 0} />
                <Stat icon={UserCheck} label="Active Visitors" value={traffic?.active_visitors_period ?? 0} hint={`${traffic?.total_visitors_all_time ?? 0} all-time`} />
                <Stat icon={Repeat} label="Returning" value={traffic?.retention?.returning ?? 0} hint={`${traffic?.retention?.new ?? 0} new`} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ChartCard title="Traffic Over Time" subtitle="Visits & page views" testId="dashboard-chart-traffic">
                    <ResponsiveContainer width="100%" height={260}>
                        <AreaChart data={trafficTs} margin={{ top: 6, right: 8, left: -12, bottom: 0 }}>
                            <defs>
                                <linearGradient id="pvGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={MAUVE} stopOpacity={0.4} />
                                    <stop offset="100%" stopColor={MAUVE} stopOpacity={0.02} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(90,36,50,0.08)" />
                            <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11, fill: '#9A8088' }} interval="preserveStartEnd" minTickGap={24} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#9A8088' }} />
                            <Tooltip contentStyle={tooltipStyle} labelFormatter={fmtDate} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Area type="monotone" dataKey="pageviews" name="Page Views" stroke={MAUVE} strokeWidth={2} fill="url(#pvGrad)" />
                            <Area type="monotone" dataKey="visits" name="Visits" stroke={BURGUNDY} strokeWidth={2} fillOpacity={0} />
                        </AreaChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Conversion Funnel" subtitle="Visit → request" testId="dashboard-chart-funnel">
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={funnel} layout="vertical" margin={{ top: 6, right: 16, left: 20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(90,36,50,0.08)" horizontal={false} />
                            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#9A8088' }} />
                            <YAxis type="category" dataKey="name" width={92} tick={{ fontSize: 11, fill: '#5E2134' }} />
                            <Tooltip contentStyle={tooltipStyle} />
                            <Bar dataKey="value" name="Count" fill={BURGUNDY} radius={[0, 4, 4, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                <ChartCard title="New vs Returning" subtitle="Retention" testId="dashboard-chart-retention">
                    {(traffic?.retention?.new || traffic?.retention?.returning) ? (
                        <ResponsiveContainer width="100%" height={260}>
                            <PieChart>
                                <Pie data={retention} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                                    <Cell fill={BURGUNDY} />
                                    <Cell fill={MAUVE} />
                                </Pie>
                                <Tooltip contentStyle={tooltipStyle} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <p className="text-sm py-12 text-center" style={{ color: 'var(--ink-muted)' }}>No visitor data yet.</p>
                    )}
                </ChartCard>

                <ChartCard title="Most Viewed Products" subtitle="By product views" testId="dashboard-chart-top-viewed">
                    {topViewed.length === 0 ? (
                        <p className="text-sm py-12 text-center" style={{ color: 'var(--ink-muted)' }}>No product views yet.</p>
                    ) : (
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={topViewed} layout="vertical" margin={{ top: 6, right: 16, left: 8, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(90,36,50,0.08)" horizontal={false} />
                                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#9A8088' }} />
                                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: '#5E2134' }} />
                                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [v, 'Views']} />
                                <Bar dataKey="views" name="Views" fill={MAUVE} radius={[0, 4, 4, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>
            </div>

            {/* Recent requests */}
            <div className="luxury-card p-6 mt-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <p className="eyebrow" style={{ color: 'var(--ink-muted)' }}>Latest</p>
                        <h2 className="font-serif text-2xl" style={{ color: 'var(--brand-burgundy)' }}>Recent Purchase Requests</h2>
                    </div>
                    <Link to="/admin/requests" className="eyebrow inline-flex items-center gap-1" style={{ color: 'var(--brand-burgundy)' }}><TrendingUp className="w-4 h-4" /> View all &rsaquo;</Link>
                </div>
                {data.recent_requests.length === 0 ? (
                    <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>No requests yet.</p>
                ) : (
                    <ul className="divide-y divide-[rgba(90,36,50,0.08)]">
                        {data.recent_requests.map((r) => (
                            <li key={r.id} className="py-3 flex items-center justify-between gap-4" data-testid={`dashboard-recent-${r.reference}`}>
                                <div>
                                    <p className="font-serif text-base" style={{ color: 'var(--brand-burgundy)' }}>{r.customer_name}</p>
                                    <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>{r.reference} · {r.city}, {r.country}</p>
                                </div>
                                <div className="text-right">
                                    <span className="text-xs px-2.5 py-1 rounded-full" style={{ backgroundColor: 'var(--brand-rose)', color: 'var(--brand-burgundy)' }}>{r.status}</span>
                                    <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>{format(r.subtotal || 0)}</p>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
